import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useAuth, API } from "../lib/auth";
import { useI18n } from "../lib/i18n";

const STATUS_OPTIONS = ["active", "paused", "closed", "draft"];

export default function JobEditDialog({ job, open, onOpenChange, onSaved }) {
  const { authHeader } = useAuth();
  const { lang } = useI18n();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [employers, setEmployers] = useState([]);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/admin/users?role=employer`, { headers: authHeader })
      .then(r => setEmployers(r.data || []))
      .catch(() => setEmployers([]));
  }, [open, authHeader]);

  useEffect(() => {
    if (job) {
      setForm({
        title: job.title || "",
        location: job.location || "",
        contract_type: job.contract_type || "",
        sector: job.sector || "",
        salary_min: job.salary_min ?? "",
        salary_max: job.salary_max ?? "",
        description: job.description || "",
        requirements: job.requirements || "",
        benefits: job.benefits || "",
        application_url: job.application_url || "",
        status: job.status || "active",
        employer_id: job.employer_id || "",
      });
    } else {
      setForm(null);
    }
  }, [job]);

  const save = async () => {
    if (!job || !form) return;
    setSaving(true);
    try {
      const payload = { ...form };
      payload.salary_min = payload.salary_min === "" ? null : Number(payload.salary_min);
      payload.salary_max = payload.salary_max === "" ? null : Number(payload.salary_max);
      payload.application_url = payload.application_url?.trim() || null;
      // Only send employer_id if it changed
      if (payload.employer_id === job.employer_id) delete payload.employer_id;
      const r = await axios.patch(`${API}/admin/jobs/${job.id}`, payload, { headers: authHeader });
      toast.success(lang === "fr" ? "Offre mise à jour" : "Job updated");
      onSaved && onSaved(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "fr" ? "Échec de mise à jour" : "Update failed"));
    } finally { setSaving(false); }
  };

  if (!form || !job) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="job-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {lang === "fr" ? "Modifier l'offre" : "Edit job"} — {job.title}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Label>{lang === "fr" ? "Titre" : "Title"}</Label>
            <Input data-testid="job-edit-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <Label>{lang === "fr" ? "Employeur" : "Employer"}</Label>
            <Select value={form.employer_id} onValueChange={(v) => setForm({ ...form, employer_id: v })}>
              <SelectTrigger data-testid="job-edit-employer"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent className="max-h-60 overflow-y-auto">
                {employers.map(e => (
                  <SelectItem key={e.id} value={e.id}>{e.company_name} ({e.email})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-slate-500 mt-1">
              {lang === "fr" ? "Changer pour réassigner cette offre à un autre employeur." : "Change to re-assign this job to another employer."}
            </p>
          </div>
          <div>
            <Label>{lang === "fr" ? "Lieu" : "Location"}</Label>
            <Input data-testid="job-edit-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          </div>
          <div>
            <Label>{lang === "fr" ? "Type de contrat" : "Contract type"}</Label>
            <Input data-testid="job-edit-contract" value={form.contract_type} onChange={(e) => setForm({ ...form, contract_type: e.target.value })} />
          </div>
          <div>
            <Label>{lang === "fr" ? "Secteur" : "Sector"}</Label>
            <Input data-testid="job-edit-sector" value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} />
          </div>
          <div>
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="job-edit-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{lang === "fr" ? "Salaire min" : "Salary min"}</Label>
            <Input type="number" data-testid="job-edit-salmin" value={form.salary_min} onChange={(e) => setForm({ ...form, salary_min: e.target.value })} />
          </div>
          <div>
            <Label>{lang === "fr" ? "Salaire max" : "Salary max"}</Label>
            <Input type="number" data-testid="job-edit-salmax" value={form.salary_max} onChange={(e) => setForm({ ...form, salary_max: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <Label>{lang === "fr" ? "Lien de candidature externe (optionnel)" : "External apply URL (optional)"}</Label>
            <Input
              data-testid="job-edit-application-url"
              type="url"
              placeholder="https://yourcompany.com/careers/apply/123"
              value={form.application_url}
              onChange={(e) => setForm({ ...form, application_url: e.target.value })}
            />
            <p className="text-[10px] text-slate-500 mt-1">
              {lang === "fr"
                ? "Si renseigné, le bouton Postuler redirigera vers ce lien."
                : "If set, the Apply button will redirect to this URL."}
            </p>
          </div>
          <div className="md:col-span-2">
            <Label>{lang === "fr" ? "Description" : "Description"}</Label>
            <Textarea rows={5} data-testid="job-edit-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <Label>{lang === "fr" ? "Exigences" : "Requirements"}</Label>
            <Textarea rows={3} data-testid="job-edit-requirements" value={form.requirements} onChange={(e) => setForm({ ...form, requirements: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <Label>{lang === "fr" ? "Avantages" : "Benefits"}</Label>
            <Textarea rows={3} data-testid="job-edit-benefits" value={form.benefits} onChange={(e) => setForm({ ...form, benefits: e.target.value })} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">
            {lang === "fr" ? "Annuler" : "Cancel"}
          </Button>
          <Button data-testid="job-edit-save" onClick={save} disabled={saving} className="bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full">
            {saving ? (lang === "fr" ? "Enregistrement…" : "Saving…") : (lang === "fr" ? "Enregistrer" : "Save changes")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
